/**
 * overlay-toggle-widget.ts — v4M6.2 strategic-overlay toggle row.
 *
 * Five-button segmented control sitting just below the top HUD bar on
 * the left side: None / Production / Threat / Exploration / Diplomatic.
 * Highlights the active overlay. Click mutates `UiState.galaxyOverlay`
 * and persists the choice via `saveGalaxyOverlay`. Hotkeys P / T / E /
 * D do the same — they're wired in `src/ui/input.ts` and update the
 * same UiState field this widget reads, so the visual stays in sync
 * without an explicit event channel.
 *
 * Visual + click-through pattern mirrors `speed-widget.ts`: stateless
 * widget that re-reads its source-of-truth on every `update()` call.
 */

import { saveGalaxyOverlay } from '../render/galaxy-overlays';
import type { GalaxyOverlay, UiState } from './state';

export interface OverlayToggleWidgetHandle {
  update(): void;
  destroy(): void;
}

const ACTIVE_BG = '#2B4A7A';
const ACTIVE_BORDER = '#3F6FA8';
const ACTIVE_TEXT = '#FFFFFF';
const NORMAL_BG = '#0E131C';
const NORMAL_BORDER = '#2A3344';
const NORMAL_TEXT = '#D7DEEA';

interface ButtonSpec {
  value: GalaxyOverlay;
  label: string;
  tooltip: string;
}

const BUTTONS: readonly ButtonSpec[] = [
  {
    value: 'none',
    label: 'None',
    tooltip:
      'Default galaxy view with no strategic overlay. Press the same overlay hotkey again to return here.',
  },
  {
    value: 'production',
    label: 'Production',
    tooltip:
      'Heat-map of your per-tick income (credits + minerals + research + fuel) by system. Hotkey: P.',
  },
  {
    value: 'threat',
    label: 'Threat',
    tooltip:
      'Heat-map of visible enemy strength by system, weighted by hull and damage potential. Hotkey: T.',
  },
  {
    value: 'exploration',
    label: 'Exploration',
    tooltip:
      'Highlights scouted vs unscouted systems so you can plan exploration sweeps. Hotkey: E.',
  },
  {
    value: 'diplomatic',
    label: 'Diplomatic',
    tooltip:
      'Per-system dominant diplomatic state — own, federated, vassal, trading, courting, at-war, or neutral. Hotkey: D.',
  },
];

export function mountOverlayToggleWidget(
  parent: HTMLElement,
  uiState: UiState,
): OverlayToggleWidgetHandle {
  const root = document.createElement('div');
  root.id = 'hud-overlay-toggle';
  Object.assign(root.style, {
    position: 'fixed',
    top: '50px',
    left: '16px',
    height: '26px',
    display: 'inline-flex',
    alignItems: 'stretch',
    gap: '0',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    fontSize: '11px',
    letterSpacing: '0.04em',
    zIndex: '500',
  } as Partial<CSSStyleDeclaration>);

  function makeButton(spec: ButtonSpec): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = spec.label;
    btn.title = spec.tooltip;
    Object.assign(btn.style, {
      padding: '0 10px',
      background: NORMAL_BG,
      color: NORMAL_TEXT,
      border: `1px solid ${NORMAL_BORDER}`,
      borderRadius: '0',
      fontFamily: 'inherit',
      fontSize: 'inherit',
      letterSpacing: 'inherit',
      cursor: 'pointer',
      minWidth: '54px',
      transition:
        'background 100ms ease-out, border-color 100ms ease-out, color 100ms ease-out',
    } as Partial<CSSStyleDeclaration>);
    btn.addEventListener('click', () => {
      uiState.galaxyOverlay = spec.value;
      saveGalaxyOverlay(spec.value);
      paint();
    });
    return btn;
  }

  const buttons = BUTTONS.map(makeButton);

  // Segmented-control rounding — first button leftmost, last rightmost.
  if (buttons.length > 0) {
    buttons[0]!.style.borderTopLeftRadius = '2px';
    buttons[0]!.style.borderBottomLeftRadius = '2px';
    buttons[buttons.length - 1]!.style.borderTopRightRadius = '2px';
    buttons[buttons.length - 1]!.style.borderBottomRightRadius = '2px';
  }
  // Collapse adjacent borders so the buttons read as one control.
  for (let i = 1; i < buttons.length; i++) {
    buttons[i]!.style.borderLeft = 'none';
  }

  for (const btn of buttons) root.appendChild(btn);
  parent.appendChild(root);

  function paint(): void {
    for (let i = 0; i < BUTTONS.length; i++) {
      const spec = BUTTONS[i]!;
      const btn = buttons[i]!;
      const isActive = uiState.galaxyOverlay === spec.value;
      btn.style.background = isActive ? ACTIVE_BG : NORMAL_BG;
      btn.style.borderColor = isActive ? ACTIVE_BORDER : NORMAL_BORDER;
      btn.style.color = isActive ? ACTIVE_TEXT : NORMAL_TEXT;
    }
  }

  paint();

  return {
    update: paint,
    destroy: () => {
      root.remove();
    },
  };
}
